import qs from 'qs';
import { AxiosInstance } from 'axios';
import { CollectionResource } from '@app/data/resources';
import {
    CreateTaskCategoryInput,
    SetTaskCategoryComplexitySettingsInput,
    TaskCategoryFeaturedBlockFilterInput,
    TaskCategoryFilterInput,
    UpdateAttachedTaskCategoryGroupsInput,
    UpdateTaskCategoryInput,
} from './inputs';
import { TaskCategoryIconHashRef, TaskCategoryImageHashRef, TaskCategoryRef } from './refs';
import { TaskCategoryFeaturedBlockResource, TaskCategoryListItemResource, TaskCategoryResource } from './resources';

export default class TaskCategory {
    private basePath = '/api/task-category';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Get list of featured blocks for workspace
    getFeaturedBlockList = async (
        filter?: TaskCategoryFeaturedBlockFilterInput,
    ): Promise<CollectionResource<TaskCategoryFeaturedBlockResource, {}>> => {
        // Normalize workspace ID
        if (filter !== undefined && filter.workspace_id === null) {
            filter.workspace_id = '0';
        }

        const query = qs.stringify({ ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/featured-block?${query}`);

        return data;
    };

    // Get task category list
    get = async (filter?: TaskCategoryFilterInput): Promise<CollectionResource<TaskCategoryListItemResource, {}>> => {
        const query = qs.stringify({ ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/?${query}`);

        return data;
    };

    // Get single task category
    getById = async (id: ID): Promise<TaskCategoryResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}`);

        return data;
    };

    // Create new task category
    create = async (input: CreateTaskCategoryInput): Promise<TaskCategoryRef> => {
        const { data } = await this.http.post(`${this.basePath}/`, input);

        return data;
    };

    // Patch task category data
    update = async (id: ID, input: UpdateTaskCategoryInput): Promise<TaskCategoryRef> => {
        const { data } = await this.http.patch(`${this.basePath}/${id}`, input);

        return data;
    };

    // Upload and set task category icon
    uploadAndSetIcon = async (id: ID, image: File): Promise<TaskCategoryIconHashRef> => {
        // Prepare form
        const form = new FormData();
        form.append('file', image);

        // Upload
        const { data } = await this.http.post(`${this.basePath}/${id}/icon`, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return data;
    };

    // Removes current task category icon
    removeIcon = async (id: ID): Promise<TaskCategoryRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/icon`);

        return data;
    };

    // Upload and set task category image
    uploadAndSetImage = async (id: ID, image: File): Promise<TaskCategoryImageHashRef> => {
        // Prepare form
        const form = new FormData();
        form.append('file', image);

        // Upload
        const { data } = await this.http.post(`${this.basePath}/${id}/image`, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return data;
    };

    // Removes current task category image
    removeImage = async (id: ID): Promise<TaskCategoryRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/image`);

        return data;
    };

    // Archive task category
    archive = async (id: ID): Promise<TaskCategoryRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}`);

        return data;
    };

    // Restore archived task category
    restore = async (id: ID): Promise<TaskCategoryRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/restore`);

        return data;
    };

    // Update attached task category groups
    updateAttachedTaskCategoryGroups = async (
        id: ID,
        input: UpdateAttachedTaskCategoryGroupsInput,
    ): Promise<TaskCategoryRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/group`, input);

        return data;
    };

    // Set task category complexity settings
    setComplexitySettings = async (id: ID, input: SetTaskCategoryComplexitySettingsInput): Promise<TaskCategoryRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/complexity`, input);
        return data;
    };
}
